import { Fragment, createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useCustom, usePermissions, useTranslate } from "@refinedev/core";
import { PencilIcon, PlusIcon } from "@fawkes/ui";
import type { LucideIcon } from "lucide-react";
import {
  AlertTriangle,
  ArrowRight,
  Check,
  CheckCircle2,
  Copy,
  FileText,
  Info,
  Maximize2,
  Minus,
  PawPrint,
  Plus,
  Settings,
  TrendingDown,
  TrendingUp,
} from "lucide-react";

import { fetchMediaBlob, type MediaItem } from "../../api/media";
import { formatEuro } from "../../i18n/format";
import type { PensionDier } from "../pensionKlantTypes";
import { GewichtGrafiek } from "./GewichtGrafiek";
import { NieuweWeging } from "./GewichtOnderdeel";
import { VoedingWijzigen, type Plan } from "./VoedingOnderdeel";
import { ChipWijzigen, type Basis } from "./ChipPaspoortOnderdeel";
import { BijzonderhedenWijzigen } from "./BijzonderhedenWijzigen";
import type { ActieSoort } from "./dierComponenten";
import {
  LEEG,
  behandelStaat,
  cijferDatum,
  datumMetJaar,
  entingStaat,
  kenmerkToon,
  leeftijdJaren,
  lijst,
  periode,
  type Context,
  type Staat,
} from "./hulp";
import {
  TEGEL_PER_SLEUTEL,
  optie,
  regelDef,
  type TegelInstelling,
  type TegelSleutel,
  type Tint,
} from "./tegelDefinities";

/** Wat een tegel buiten zichzelf kan laten gebeuren. */
export interface TegelActies {
  /** Opent de echte tegel uit het dierdossier in een venster. */
  openDossierTegel: (tegel: DossierSleutel) => void;
  /** Springt naar een subtabblad van het dier. */
  naarSubtab: (sub: "notities") => void;
  /** Naar het tabblad Pension van de klant, waar alle boekingen staan. */
  naarPension: () => void;
  /** Na een snelle actie: de gegevens van de klant opnieuw ophalen, zodat de tegels bijtrekken. */
  gewijzigd: () => void;
}

/** De echte tegels uit het dierdossier die het overzicht groot kan openen. */
export type DossierSleutel = "basis" | "media" | "verzorging" | "medisch" | "gewicht" | "activiteiten";

/**
 * Welke dossiertegel een tegel van het overzicht groot opent. Bijzonderheden staan in de basisgegevens;
 * het verblijf heeft geen dossiertegel, dat verwijst naar het tabblad Pension.
 */
const DOSSIER_VAN: Record<TegelSleutel, DossierSleutel | null> = {
  basis: "basis",
  verzorging: "verzorging",
  medisch: "medisch",
  gewicht: "gewicht",
  bijzonderheden: "basis",
  fotos: "media",
  activiteiten: "activiteiten",
  verblijf: null,
};

/**
 * De acties van het dieroverzicht, voor de rand van elke tegel. Buiten het overzicht — het voorbeeld in
 * het instellingenvenster — is er geen, en dan krijgt een tegel ook geen knop om hem te openen.
 */
export const TegelActiesContext = createContext<TegelActies | null>(null);

export interface TegelProps {
  dier: PensionDier;
  inst: TegelInstelling;
  ctx: Context;
  acties: TegelActies;
  onInstellen: () => void;
}

// ── De rand om elke tegel ────────────────────────────────────────────────────────────────────────

function Tegel({
  sleutel,
  ctx,
  onInstellen,
  children,
}: {
  sleutel: TegelSleutel;
  ctx: Context;
  onInstellen: () => void;
  children: ReactNode;
}) {
  const def = TEGEL_PER_SLEUTEL[sleutel];
  const titel = ctx.t(def.titel);
  const acties = useContext(TegelActiesContext);
  const dossier = acties ? DOSSIER_VAN[sleutel] : null;
  const open = acties && dossier ? () => acties.openDossierTegel(dossier) : undefined;
  return (
    <section className="pk-tegel" data-tegel={sleutel} aria-label={titel}>
      <header className="pk-tegel__kop">
        <Rondje icoon={def.icoon} tint={def.tint} grootte="kop" />
        {/* De titel opent de tegel voor wie met de muis werkt; de knop ernaast doet hetzelfde en is er
            voor het toetsenbord. */}
        <h3 className={open ? "pk-tegel__titel--klikbaar" : undefined} onClick={open}>
          {titel}
        </h3>
        {open && (
          <button
            type="button"
            className="pk-tegel__tandwiel pk-tegel__open"
            onClick={open}
            aria-label={ctx.t("pensionKlant.tegel.openAria", { args: [titel] })}
            title={ctx.t("pensionKlant.tegel.open")}
          >
            <Maximize2 size={15} strokeWidth={1.8} aria-hidden="true" />
          </button>
        )}
        <button
          type="button"
          className="pk-tegel__tandwiel"
          onClick={onInstellen}
          aria-label={ctx.t("pensionKlant.tegel.instellenAria", { args: [titel] })}
          title={ctx.t("pensionKlant.tegel.instellen")}
        >
          <Settings size={16} strokeWidth={1.8} aria-hidden="true" />
        </button>
      </header>
      <div className="pk-tegel__inhoud">{children}</div>
    </section>
  );
}

/**
 * Een icoon in een zacht gekleurd rondje. Elke tegel en elk onderdeel heeft zijn eigen kleur, zodat je
 * op een vol scherm aan de kleur ziet waar je kijkt — dezelfde vorm als de rondjes in de kop van de
 * klant.
 */
export function Rondje({ icoon: Icoon, tint, grootte = "regel" }: { icoon: LucideIcon; tint: Tint; grootte?: "kop" | "regel" }) {
  return (
    <span className="pk-tint pk-tint--rond" data-tint={tint} data-grootte={grootte} aria-hidden="true">
      <Icoon size={grootte === "kop" ? 16 : 13} strokeWidth={2} />
    </span>
  );
}

interface Rij {
  sleutel: string;
  label: string;
  icoon?: LucideIcon;
  tint?: Tint;
  waarde: ReactNode;
  actie?: RijActie;
}

/**
 * De snelle actie achter een regel: invullen zonder de hele dossiertegel open te hoeven klappen.
 *
 * <p>Een plus voegt een registratie toe aan de historie (een weging), een potlood overschrijft vaste
 * informatie (het voedingsschema, het chipnummer). Dat onderscheid staat in de componentbibliotheek van
 * de samengestelde diertegels en wordt hier alleen getoond -- wie de twee door elkaar haalt, krijgt een
 * historie waarin gisteren ineens iets anders staat dan gisteren.
 *
 * <p>Zonder schrijfrecht geeft een tegel gewoon geen actie mee: de regel blijft staan, de knop
 * verdwijnt. Informatie weglaten omdat iemand niet mag schrijven leest als een gat in het dossier.
 */
interface RijActie {
  soort: ActieSoort;
  onClick: () => void;
}

/** Regels met een gekleurd icoon, het label en de waarde, in de volgorde die de gebruiker koos. */
function Rijen({ rijen }: { rijen: Rij[] }) {
  if (rijen.length === 0) return null;
  return (
    <dl className="pk-rijen">
      {rijen.map((r) => (
        <div key={r.sleutel} className="pk-rij">
          <dt>
            {r.icoon && r.tint && <Rondje icoon={r.icoon} tint={r.tint} />}
            {r.label}
          </dt>
          <dd>
            {r.waarde}
            {r.actie ? <RijActieKnop rij={r} actie={r.actie} /> : null}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** Het knopje achter een regel; het icoon zegt of het bijschrijft of overschrijft. */
function RijActieKnop({ rij, actie }: { rij: Rij; actie: RijActie }) {
  const t = useTranslate();
  return (
    <button
      type="button"
      className="pk-rij__actie"
      data-testid={`rij-actie-${rij.sleutel}`}
      onClick={actie.onClick}
      aria-label={`${t(`dierTegel.actie.${actie.soort}`)}: ${rij.label}`}
      title={t(`dierTegel.actie.${actie.soort}`)}
    >
      {actie.soort === "registratie" ? <PlusIcon width={14} height={14} /> : <PencilIcon width={14} height={14} />}
    </button>
  );
}

/** De gekozen onderdelen van een tegel, elk met zijn icoon, kleur en waarde. */
function kiesRijen(
  tegel: TegelSleutel,
  inst: TegelInstelling,
  t: Context["t"],
  waarden: Record<string, () => ReactNode>,
  acties: Record<string, RijActie | undefined> = {},
): Rij[] {
  return (inst.regels ?? [])
    .filter((s) => s in waarden)
    .map((s) => {
      const def = regelDef(tegel, s);
      return {
        sleutel: s,
        label: t(`pensionKlant.regel.${s}`),
        icoon: def?.icoon,
        tint: def?.tint,
        waarde: waarden[s](),
        actie: acties[s],
      };
    });
}

/** Een vol groen rondje met een wit vinkje, zoals in het ontwerp — een omlijnd rondje leest als "leeg". */
export function VolVinkje({ size = 15 }: { size?: number }) {
  return <CheckCircle2 size={size} fill="currentColor" stroke="var(--pk-vlak)" strokeWidth={2.4} aria-hidden="true" />;
}

/** Een groen vinkje bij goed nieuws, amber bij aandacht; zonder kleur als er niets te melden is. */
function StaatWaarde({ staat, tot, ctx }: { staat: Staat; tot?: string | null; ctx: Context }) {
  const { t, locale } = ctx;
  if (staat === "inOrde") {
    return (
      <span className="pk-staat" data-toon="groen">
        <VolVinkje />
        {t("pensionKlant.staat.inOrde")}
      </span>
    );
  }
  if (staat === "verlopen") {
    return (
      <span className="pk-staat" data-toon="amber">
        <AlertTriangle size={14} aria-hidden="true" />
        {t("pensionKlant.staat.verlopen")}
      </span>
    );
  }
  if (staat === "binnenkort") {
    return (
      <span className="pk-staat" data-toon="amber">
        <AlertTriangle size={14} aria-hidden="true" />
        {t("pensionKlant.staat.verlooptOp", { args: [datumMetJaar(tot, locale)] })}
      </span>
    );
  }
  return <>{LEEG}</>;
}

/** Het chipnummer met een knop die het kopieert; je typt het anders over bij de dierenarts. */
export function KopieerKnop({ tekst, ctx }: { tekst: string; ctx: Context }) {
  const [gekopieerd, setGekopieerd] = useState(false);
  useEffect(() => {
    if (!gekopieerd) return;
    const id = window.setTimeout(() => setGekopieerd(false), 1500);
    return () => window.clearTimeout(id);
  }, [gekopieerd]);
  return (
    <button
      type="button"
      className="pk-kopieer"
      aria-label={ctx.t(gekopieerd ? "pensionKlant.kopieer.klaar" : "pensionKlant.kopieer.aria", { args: [tekst] })}
      title={ctx.t(gekopieerd ? "pensionKlant.kopieer.klaar" : "pensionKlant.kopieer.titel")}
      onClick={() => {
        void navigator.clipboard?.writeText(tekst).then(() => setGekopieerd(true));
      }}
    >
      {gekopieerd ? <Check size={15} aria-hidden="true" /> : <Copy size={15} aria-hidden="true" />}
    </button>
  );
}

// ── Basisgegevens ────────────────────────────────────────────────────────────────────────────────

/**
 * Wat de tegel uit het basisrecord leest.
 *
 * <p>Het erft van {@code Basis}, het record dat bij een wijziging weer helemaal terug moet: de api werkt
 * de kaart in een keer bij, dus wie chip en paspoort wil wijzigen moet de rest ongemoeid kunnen
 * meesturen. Twee losse typen zou betekenen dat een nieuw veld op de kaart hier stil wegvalt.
 */
interface AnimalBasis extends Basis {
  kleur?: string | null;
  registratieNummer?: string | null;
  vacht?: string | null;
}

/** Een tekstveld uit het dossier; leeg of alleen spaties is "niet vastgelegd". */
function tekst(waarde: string | null | undefined): string {
  return waarde && waarde.trim() ? waarde : LEEG;
}

export function BasisTegel({ dier, inst, ctx, acties, onInstellen }: TegelProps) {
  const { t } = ctx;
  const { data, refetch } = useCustom<AnimalBasis>({ url: `animals/${dier.id}`, method: "get" });
  const basis = data?.data;
  const jaren = leeftijdJaren(dier.geboortedatum, ctx.vandaag);
  const { data: rechten } = usePermissions<string[]>();
  const magWijzigen = (rechten ?? []).includes("ROLE_ANIMAL_EDIT");
  const [chipVenster, setChipVenster] = useState(false);
  // Het potlood pas aanbieden als het basisrecord binnen is: zonder dat record zou de wijziging de
  // rest van de kaart leegmaken.
  const chipActie: RijActie | undefined =
    magWijzigen && basis ? { soort: "wijziging", onClick: () => setChipVenster(true) } : undefined;
  const rijen = kiesRijen("basis", inst, t, {
    ras: () => tekst(dier.ras),
    kleur: () => tekst(basis?.kleur),
    geboortedatum: () =>
      dier.geboortedatum
        ? `${cijferDatum(dier.geboortedatum)}${jaren != null ? ` (${t("pensionKlant.dieren.jaar", { args: [jaren] })})` : ""}`
        : LEEG,
    geslacht: () =>
      dier.geslacht
        ? `${dier.geslacht} (${t(dier.gecastreerd ? "pensionKlant.regel.gecastreerd" : "pensionKlant.regel.nietGecastreerd")})`
        : LEEG,
    chipnummer: () =>
      dier.chipnummer ? (
        <span className="pk-met-knop">
          {dier.chipnummer}
          <KopieerKnop tekst={dier.chipnummer} ctx={ctx} />
        </span>
      ) : (
        LEEG
      ),
    paspoortnummer: () => tekst(basis?.paspoortNummer),
    soort: () => tekst(dier.diersoort),
    vacht: () => tekst(basis?.vacht),
    registratienummer: () => tekst(basis?.registratieNummer),
    dierenarts: () => tekst(dier.dierenarts),
  }, { chipnummer: chipActie, paspoortnummer: chipActie });
  return (
    <Tegel sleutel="basis" ctx={ctx} onInstellen={onInstellen}>
      <Rijen rijen={rijen} />
      {chipVenster && basis ? (
        <ChipWijzigen
          dierId={dier.id}
          basis={basis}
          onSluit={() => setChipVenster(false)}
          onOpgeslagen={() => { setChipVenster(false); void refetch(); acties.gewijzigd(); }}
        />
      ) : null}
    </Tegel>
  );
}

// ── Verzorging ───────────────────────────────────────────────────────────────────────────────────

/** "08:00 · 12:00 · 18:00" → "08:00, 12:00 en 18:00". */
function schema(voertijden: string | null | undefined, t: Context["t"]): string {
  if (!voertijden) return LEEG;
  const delen = voertijden.split(" · ").map((d) => d.trim()).filter(Boolean);
  if (delen.length < 2) return voertijden;
  return `${delen.slice(0, -1).join(", ")} ${t("pensionKlant.en")} ${delen[delen.length - 1]}`;
}

export function VerzorgingTegel({ dier, inst, ctx, acties, onInstellen }: TegelProps) {
  const { t } = ctx;
  const s = dier.stand;
  const heeftPlan = !!(s?.voer || s?.voerHoeveelheid || s?.voertijden);
  const { data: rechten } = usePermissions<string[]>();
  const magVoeding = (rechten ?? []).includes("ROLE_ANIMAL_FEEDING_EDIT");
  const [voedingVenster, setVoedingVenster] = useState(false);
  // Het venster heeft het plan met zijn id en version nodig; dat staat niet in de stand, dus het wordt
  // pas opgehaald als iemand het potlood gebruikt.
  const { data: voeding } = useCustom<{ plan: Plan | null }>({
    url: `animals/${dier.id}/feeding`,
    method: "get",
    queryOptions: { enabled: voedingVenster },
  });
  // Alle vier de regels komen uit hetzelfde schema en openen dus hetzelfde venster: het potlood staat
  // naast de waarde waar je naar kijkt, welke van de vier dat ook is.
  const voedingActie: RijActie | undefined =
    magVoeding ? { soort: "wijziging", onClick: () => setVoedingVenster(true) } : undefined;
  const rijen = kiesRijen("verzorging", inst, t, {
    voeding: () =>
      s?.voer
        ? s.voerKeerPerDag
          ? t("pensionKlant.waarde.perDag", { args: [s.voer, s.voerKeerPerDag] })
          : s.voer
        : t("pensionKlant.waarde.geenSchema"),
    hoeveelheid: () => s?.voerHoeveelheid ?? LEEG,
    schema: () => (heeftPlan ? schema(s?.voertijden, t) : LEEG),
    voerBijzonderheden: () => s?.voerOpmerking ?? (heeftPlan ? t("pensionKlant.waarde.geen") : LEEG),
    gedrag: () => dier.gedrag ?? LEEG,
  }, {
    voeding: voedingActie,
    hoeveelheid: voedingActie,
    schema: voedingActie,
    voerBijzonderheden: voedingActie,
  });
  return (
    <Tegel sleutel="verzorging" ctx={ctx} onInstellen={onInstellen}>
      <Rijen rijen={rijen} />
      {voedingVenster && voeding?.data ? (
        <VoedingWijzigen
          dierId={dier.id}
          plan={voeding.data.plan}
          onSluit={() => setVoedingVenster(false)}
          onOpgeslagen={() => { setVoedingVenster(false); acties.gewijzigd(); }}
        />
      ) : null}
    </Tegel>
  );
}

// ── Medisch ──────────────────────────────────────────────────────────────────────────────────────

export function MedischTegel({ dier, inst, ctx, onInstellen }: TegelProps) {
  const { t, vandaag } = ctx;
  const s = dier.stand;
  const rijen = kiesRijen("medisch", inst, t, {
    vaccinaties: () => (
      <StaatWaarde staat={entingStaat(dier, vandaag)} tot={s?.entingVerlooptOp ?? s?.entingGeldigTot} ctx={ctx} />
    ),
    medicatie: () => {
      if (!s || s.medicatieActief === 0) return t("pensionKlant.waarde.geen");
      const naam = s.medicatieNaam
        ? s.medicatieKeerPerDag
          ? t("pensionKlant.waarde.daags", { args: [s.medicatieNaam, s.medicatieKeerPerDag] })
          : s.medicatieNaam
        : t("pensionKlant.waarde.actief");
      return s.medicatieActief > 1 ? `${naam} +${s.medicatieActief - 1}` : naam;
    },
    ontworming: () => (
      <StaatWaarde
        staat={behandelStaat(!!s?.ontworming, s?.ontwormingGeldigTot, vandaag)}
        tot={s?.ontwormingGeldigTot}
        ctx={ctx}
      />
    ),
    ontvlooien: () => (
      <StaatWaarde
        staat={behandelStaat(!!s?.ontvlooid, s?.ontvlooiingGeldigTot, vandaag)}
        tot={s?.ontvlooiingGeldigTot}
        ctx={ctx}
      />
    ),
    dierenarts: () => dier.dierenarts ?? LEEG,
  });
  return (
    <Tegel sleutel="medisch" ctx={ctx} onInstellen={onInstellen}>
      <Rijen rijen={rijen} />
    </Tegel>
  );
}

// ── Gewicht ──────────────────────────────────────────────────────────────────────────────────────

interface Meting {
  datum: string | null;
  gewicht: number | null;
}

export function GewichtTegel({ dier, inst, ctx, acties, onInstellen }: TegelProps) {
  const { t, locale, vandaag } = ctx;
  const { data, refetch } = useCustom<{ metingen?: Meting[] }>({ url: `animals/${dier.id}/weights`, method: "get" });
  const { data: rechten } = usePermissions<string[]>();
  // Wegen mag iedereen die het dier ziet; alleen een weging achteraf verschuiven is beheer, en dat
  // onderscheid maakt het invoervenster zelf.
  const magCorrigeren = (rechten ?? []).includes("ROLE_ANIMAL_EDIT");
  const [weegVenster, setWeegVenster] = useState(false);
  const punten = lijst<Meting>(data?.data?.metingen)
    .filter((m) => m.datum && m.gewicht != null)
    .map((m) => ({ datum: m.datum!.slice(0, 10), kg: Number(m.gewicht) }));
  const laatste = punten.length
    ? punten.reduce((a, b) => (a.datum >= b.datum ? a : b))
    : dier.stand?.gewichtKg != null && dier.stand.gewichtDatum
      ? { datum: dier.stand.gewichtDatum, kg: dier.stand.gewichtKg }
      : null;
  const maanden = optie<number>(inst, "gewicht", "maanden");
  const kg = (n: number) => new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(n);
  // De trend: het verschil tussen de eerste en de laatste meting in de gekozen periode.
  const [jaar, maand] = vandaag.slice(0, 7).split("-").map(Number);
  const periodeStart = new Date(Date.UTC(jaar, maand - 1 - (maanden - 1), 1)).toISOString().slice(0, 10);
  const inPeriode = punten.filter((p) => p.datum >= periodeStart).sort((a, b) => a.datum.localeCompare(b.datum));
  const eerste = inPeriode.length > 1 ? inPeriode[0] : null;
  const verschil = eerste && laatste ? laatste.kg - eerste.kg : null;
  const richting = verschil == null ? null : verschil > 0.05 ? "op" : verschil < -0.05 ? "neer" : "gelijk";
  const onderdelen: Record<string, () => ReactNode> = {
    waarde: () => (
      <div className="pk-gewicht__kop">
        <p className="pk-gewicht__waarde">{t("pensionKlant.waarde.kg", { args: [kg(laatste!.kg)] })}</p>
        {richting && eerste && verschil != null && (
          <span className="pk-gewicht__trend" data-richting={richting}>
            {richting === "op" ? (
              <TrendingUp size={14} aria-hidden="true" />
            ) : richting === "neer" ? (
              <TrendingDown size={14} aria-hidden="true" />
            ) : (
              <Minus size={14} aria-hidden="true" />
            )}
            {richting === "gelijk"
              ? t("pensionKlant.waarde.trendGelijk", { args: [datumMetJaar(eerste.datum, locale).replace(/^0/, "")] })
              : t("pensionKlant.waarde.trend", {
                  args: [
                    `${verschil > 0 ? "+" : "−"}${kg(Math.abs(verschil))} kg`,
                    datumMetJaar(eerste.datum, locale).replace(/^0/, ""),
                  ],
                })}
          </span>
        )}
      </div>
    ),
    datum: () => (
      <p className="pk-gewicht__datum">
        {t("pensionKlant.waarde.laatsteMeting", { args: [datumMetJaar(laatste!.datum, locale)] })}
      </p>
    ),
    grafiek: () => (
      <GewichtGrafiek
        // Staat er alleen een gewicht op het dier zelf, dan is dat het enige punt dat er is.
        punten={punten.length ? punten : laatste ? [laatste] : []}
        tot={vandaag}
        maanden={maanden}
        locale={locale}
        label={t("pensionKlant.waarde.grafiekAria", { args: [maanden] })}
        leegTekst={t("pensionKlant.waarde.grafiekLeeg")}
        buitenPeriodeTekst={t("pensionKlant.waarde.grafiekBuitenPeriode", { args: [maanden] })}
      />
    ),
  };

  // De plus staat ook -- juist -- bij een dier dat nog nooit gewogen is: daar is hij het meest waard,
  // en anders moet je voor de eerste weging alsnog de hele dossiertegel open.
  const plus = (
    <button
      type="button"
      className="pk-rij__actie"
      data-testid="rij-actie-weging"
      onClick={() => setWeegVenster(true)}
      aria-label={`${t("dierTegel.actie.registratie")}: ${t("pensionKlant.tegel.gewicht")}`}
      title={t("dierTegel.actie.registratie")}
    >
      <PlusIcon width={14} height={14} />
    </button>
  );

  return (
    <Tegel sleutel="gewicht" ctx={ctx} onInstellen={onInstellen}>
      {laatste ? (
        <div className="pk-gewicht">
          {(inst.regels ?? []).filter((s) => s in onderdelen).map((s) => (
            <Fragment key={s}>{onderdelen[s]()}</Fragment>
          ))}
          <div className="pk-gewicht__acties">{plus}</div>
        </div>
      ) : (
        <p className="pk-tegel__leeg">
          {t("pensionKlant.waarde.nietGewogen")}
          {plus}
        </p>
      )}
      {weegVenster ? (
        <NieuweWeging
          dierId={dier.id}
          magCorrigeren={magCorrigeren}
          onSluit={() => setWeegVenster(false)}
          onOpgeslagen={() => { setWeegVenster(false); void refetch(); acties.gewijzigd(); }}
        />
      ) : null}
    </Tegel>
  );
}

// ── Bijzonderheden ───────────────────────────────────────────────────────────────────────────────

export function BijzonderhedenTegel({ dier, inst, ctx, acties, onInstellen }: TegelProps) {
  const { t } = ctx;
  const kenmerken = dier.kenmerken ?? [];
  const { data: rechten } = usePermissions<string[]>();
  const magWijzigen = (rechten ?? []).includes("ROLE_ANIMAL_EDIT");
  const [venster, setVenster] = useState(false);
  // Een potlood en geen plus: de labels en de notitie zijn vaste informatie die je overschrijft, geen
  // historie waar je iets aan toevoegt. Het staat onderaan het blok, net als de plus van de gewichttegel,
  // en ook -- juist -- bij een dier zonder bijzonderheden.
  const potlood = magWijzigen ? (
    <div className="pk-bijzonderheden__acties">
      <button
        type="button"
        className="pk-rij__actie"
        data-testid="rij-actie-bijzonderheden"
        onClick={() => setVenster(true)}
        aria-label={`${t("dierTegel.actie.wijziging")}: ${t("pensionKlant.tegel.bijzonderheden")}`}
        title={t("dierTegel.actie.wijziging")}
      >
        <PencilIcon width={14} height={14} />
      </button>
    </div>
  ) : null;
  const onderdelen: Record<string, () => ReactNode> = {
    kenmerken: () =>
      kenmerken.length === 0 ? (
        <ul className="pk-labels">
          <li className="pk-label" data-toon="grijs">
            <Info size={15} aria-hidden="true" />
            {t("pensionKlant.waarde.geenBijzonderheden")}
          </li>
        </ul>
      ) : (
        <ul className="pk-labels">
          {kenmerken.map((k) => {
            const toon = kenmerkToon(k);
            return (
              <li key={k} className="pk-label" data-toon={toon}>
                {toon === "groen" ? (
                  <PawPrint size={15} aria-hidden="true" />
                ) : toon === "grijs" ? (
                  <Info size={15} aria-hidden="true" />
                ) : (
                  <AlertTriangle size={15} aria-hidden="true" />
                )}
                {k}
              </li>
            );
          })}
        </ul>
      ),
    notitie: () => (dier.bijzonderheden ? <p className="pk-tegel__notitie">{dier.bijzonderheden}</p> : null),
  };
  return (
    <Tegel sleutel="bijzonderheden" ctx={ctx} onInstellen={onInstellen}>
      {(inst.regels ?? []).filter((s) => s in onderdelen).map((s) => (
        <Fragment key={s}>{onderdelen[s]()}</Fragment>
      ))}
      {potlood}
      {venster ? (
        <BijzonderhedenWijzigen
          dierId={dier.id}
          kenmerken={kenmerken}
          notitie={dier.bijzonderheden}
          onSluit={() => setVenster(false)}
          onOpgeslagen={() => { setVenster(false); acties.gewijzigd(); }}
        />
      ) : null}
    </Tegel>
  );
}

// ── Foto's ───────────────────────────────────────────────────────────────────────────────────────

function useFotoUrls(dierId: number, items: MediaItem[]): Record<number, string> {
  const [urls, setUrls] = useState<Record<number, string>>({});
  const sleutel = items.map((i) => i.id).join(",");
  useEffect(() => {
    let weg = false;
    const gemaakt: string[] = [];
    Promise.all(
      items.map((i) =>
        fetchMediaBlob(String(dierId), i.id)
          .then((blob) => {
            const url = URL.createObjectURL(blob);
            gemaakt.push(url);
            return [i.id, url] as const;
          })
          .catch(() => null),
      ),
    ).then((paren) => {
      if (weg) return;
      setUrls(Object.fromEntries(paren.filter((p): p is readonly [number, string] => p != null)));
    });
    return () => {
      weg = true;
      gemaakt.forEach((u) => URL.revokeObjectURL(u));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dierId, sleutel]);
  return urls;
}

/**
 * Media & documenten: de nieuwste foto's als miniaturen en daaronder de nieuwste documenten als regels,
 * elk tot het ingestelde aantal. Toevoegen en een document openen gaan via de mediategel van het dossier,
 * die uploaden, bekijken en downloaden al kan. De sleutel blijft "fotos", zodat bewaarde indelingen
 * blijven gelden.
 */
export function MediaTegel({ dier, inst, ctx, acties, onInstellen }: TegelProps) {
  const { t, locale } = ctx;
  const aantal = optie<number>(inst, "fotos", "aantal");
  const { data } = useCustom<MediaItem[]>({ url: `animals/${dier.id}/media`, method: "get" });
  // Een kopie: sorteren op de lijst zelf zou de gegevens in de cache van Refine omgooien.
  const nieuwsteEerst = [...lijst<MediaItem>(data?.data)].sort((a, b) =>
    String(b.uploadedAt ?? "").localeCompare(String(a.uploadedAt ?? "")),
  );
  const isFoto = (m: MediaItem) => (m.contentType ?? "").startsWith("image/");
  const fotos = nieuwsteEerst.filter(isFoto).slice(0, aantal);
  const documenten = nieuwsteEerst.filter((m) => !isFoto(m)).slice(0, aantal);
  const urls = useFotoUrls(dier.id, fotos);
  return (
    <Tegel sleutel="fotos" ctx={ctx} onInstellen={onInstellen}>
      <ul className="pk-fotos">
        {fotos.map((f) => (
          <li key={f.id} className="pk-foto">
            {urls[f.id] ? <img src={urls[f.id]} alt={f.description ?? f.fileName ?? ""} /> : <span aria-hidden="true" />}
          </li>
        ))}
        <li>
          <button type="button" className="pk-foto pk-foto--nieuw" onClick={() => acties.openDossierTegel("media")}>
            <Plus size={18} aria-hidden="true" />
            <span>{t("pensionKlant.waarde.fotoToevoegen")}</span>
          </button>
        </li>
      </ul>
      {documenten.length > 0 && (
        <ul className="pk-docs">
          {documenten.map((d) => (
            <li key={d.id}>
              <button type="button" className="pk-doc" onClick={() => acties.openDossierTegel("media")}>
                <FileText size={15} aria-hidden="true" />
                <span className="pk-doc__naam">{d.description || d.fileName || LEEG}</span>
                <span className="pk-doc__datum">{datumMetJaar(d.uploadedAt, locale)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </Tegel>
  );
}

// ── Recente activiteiten ─────────────────────────────────────────────────────────────────────────

interface Activiteit {
  sleutel: string;
  soort: "gewicht" | "medicatie" | "notitie" | "logboek";
  datum: string;
  titel: string;
  detail: string | null;
  citaat?: boolean;
}

interface Voorschrift {
  id: string;
  naam: string | null;
  startDatumIso?: string | null;
  startDatum?: string | null;
}
interface Notitie {
  id: number;
  date: string | null;
  note: string | null;
}
interface LogboekRegel {
  id: string;
  categorieLabel: string | null;
  tekst: string | null;
  gebeurdOp: string | null;
}

/** Elke soort gebeurtenis het icoon en de kleur van zijn onderdeel, zodat je het in de instellingen terugziet. */
const ACTIVITEIT_REGEL: Record<Activiteit["soort"], string> = {
  gewicht: "actGewicht",
  medicatie: "actMedicatie",
  notitie: "actNotitie",
  logboek: "actLogboek",
};

function ActiviteitIcoon({ soort }: { soort: Activiteit["soort"] }) {
  const def = regelDef("activiteiten", ACTIVITEIT_REGEL[soort])!;
  return <Rondje icoon={def.icoon} tint={def.tint} />;
}

export function ActiviteitenTegel({ dier, inst, ctx, acties, onInstellen }: TegelProps) {
  const { t, locale } = ctx;
  const soorten = new Set(inst.regels ?? []);
  const aantal = optie<number>(inst, "activiteiten", "aantal");
  const id = dier.id;
  const gewichten = useCustom<{ metingen?: Meting[] }>({
    url: `animals/${id}/weights`,
    method: "get",
    queryOptions: { enabled: soorten.has("actGewicht") },
  }).data?.data;
  const medicatie = useCustom<{ prescriptions?: Voorschrift[] }>({
    url: `animals/${id}/medicijnen`,
    method: "get",
    queryOptions: { enabled: soorten.has("actMedicatie") },
  }).data?.data;
  const notities = useCustom<Notitie[]>({
    url: `animals/${id}/notes`,
    method: "get",
    queryOptions: { enabled: soorten.has("actNotitie") },
  }).data?.data;
  const logboek = useCustom<{ regels?: LogboekRegel[] }>({
    url: `animals/${id}/logboek`,
    method: "get",
    queryOptions: { enabled: soorten.has("actLogboek") },
  }).data?.data;

  const kg = (n: number) => new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(n);
  const alles: Activiteit[] = [];
  if (soorten.has("actGewicht")) {
    for (const m of lijst<Meting>(gewichten?.metingen)) {
      if (!m.datum || m.gewicht == null) continue;
      alles.push({
        sleutel: `g${m.datum}`,
        soort: "gewicht",
        datum: m.datum,
        titel: t("pensionKlant.activiteit.gewicht"),
        detail: t("pensionKlant.waarde.kg", { args: [kg(Number(m.gewicht))] }),
      });
    }
  }
  if (soorten.has("actMedicatie")) {
    for (const p of lijst<Voorschrift>(medicatie?.prescriptions)) {
      const d = p.startDatumIso ?? p.startDatum;
      if (!d) continue;
      alles.push({ sleutel: `m${p.id}`, soort: "medicatie", datum: d, titel: t("pensionKlant.activiteit.medicatie"), detail: p.naam });
    }
  }
  if (soorten.has("actNotitie")) {
    for (const n of lijst<Notitie>(notities)) {
      if (!n.date) continue;
      alles.push({
        sleutel: `n${n.id}`,
        soort: "notitie",
        datum: n.date,
        titel: t("pensionKlant.activiteit.notitie"),
        detail: n.note,
        citaat: true,
      });
    }
  }
  if (soorten.has("actLogboek")) {
    for (const l of lijst<LogboekRegel>(logboek?.regels)) {
      if (!l.gebeurdOp) continue;
      alles.push({
        sleutel: `l${l.id}`,
        soort: "logboek",
        datum: l.gebeurdOp,
        titel: l.categorieLabel ?? t("pensionKlant.activiteit.logboek"),
        detail: l.tekst,
      });
    }
  }
  const recent = alles.sort((a, b) => b.datum.localeCompare(a.datum)).slice(0, aantal);

  return (
    <Tegel sleutel="activiteiten" ctx={ctx} onInstellen={onInstellen}>
      {recent.length === 0 ? (
        <p className="pk-tegel__leeg">{t("pensionKlant.activiteit.geen")}</p>
      ) : (
        <ol className="pk-activiteiten">
          {recent.map((a) => (
            <li key={a.sleutel} className="pk-activiteit" data-soort={a.soort}>
              <ActiviteitIcoon soort={a.soort} />
              <time className="pk-activiteit__datum" dateTime={a.datum.slice(0, 10)}>
                {datumMetJaar(a.datum, locale)}
              </time>
              <span className="pk-activiteit__tekst">
                <strong>{a.titel}</strong>
                {a.detail && (
                  <span className="pk-activiteit__detail" data-citaat={a.citaat || undefined}>
                    {a.citaat ? `“${a.detail}”` : a.detail}
                  </span>
                )}
              </span>
            </li>
          ))}
        </ol>
      )}
      <button type="button" className="pk-tegel__verder" onClick={() => acties.naarSubtab("notities")}>
        {t("pensionKlant.activiteit.alle")}
        <ArrowRight size={15} aria-hidden="true" />
      </button>
    </Tegel>
  );
}

// ── Huidig / volgend verblijf ────────────────────────────────────────────────────────────────────

export function VerblijfTegel({ dier, inst, ctx, acties, onInstellen }: TegelProps) {
  const { t, locale } = ctx;
  const res = dier.huidigeReservering;
  const status =
    dier.status === "IN_PENSION"
      ? { tekst: t("pensionKlant.status.inPension"), toon: "groen" }
      : dier.status === "KOMENDE_RESERVERING"
        ? { tekst: t("pensionKlant.status.gereserveerd"), toon: "groen" }
        : { tekst: t("pensionKlant.status.geenReservering"), toon: "grijs" };
  const rijen = kiesRijen("verblijf", inst, t, {
    status: () => (
      <span className="pk-pil" data-toon={status.toon}>
        {status.tekst}
      </span>
    ),
    periode: () => (res ? periode(res.van, res.tot, locale) : LEEG),
    kennel: () => (res ? (res.kennel ?? t("pensionKlant.waarde.nietIngedeeld")) : LEEG),
    bezoeknummer: () =>
      dier.bezoekNummer > 0 ? t("pensionKlant.dieren.bezoek", { args: [dier.bezoekNummer] }) : t("pensionKlant.dieren.eersteBezoek"),
    nachten: () => (res ? t(res.nachten === 1 ? "pensionKlant.waarde.nacht" : "pensionKlant.waarde.nachten", { args: [res.nachten] }) : LEEG),
    tarief: () => {
      const laag = dier.prijs?.dagtariefLaag || null;
      return laag ? t("pensionKlant.waarde.perDagBedrag", { args: [formatEuro(laag, locale)] }) : LEEG;
    },
    totaal: () => (res?.totaalBedrag ? formatEuro(res.totaalBedrag, locale) : LEEG),
  });
  return (
    <Tegel sleutel="verblijf" ctx={ctx} onInstellen={onInstellen}>
      <Rijen rijen={rijen} />
      <button type="button" className="pk-tegel__verder" onClick={() => acties.naarPension()}>
        {t("pensionKlant.verblijf.open")}
        <ArrowRight size={15} aria-hidden="true" />
      </button>
    </Tegel>
  );
}

/** Welke component bij welke tegel hoort. */
export const TEGEL_COMPONENT: Record<TegelSleutel, (p: TegelProps) => ReactNode> = {
  basis: BasisTegel,
  verzorging: VerzorgingTegel,
  medisch: MedischTegel,
  gewicht: GewichtTegel,
  bijzonderheden: BijzonderhedenTegel,
  fotos: MediaTegel,
  activiteiten: ActiviteitenTegel,
  verblijf: VerblijfTegel,
};
